import { useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
} from "recharts";

const TICK = { fill: "#8A87A8", fontSize: 11 };

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function formatInt(value) {
    return Number(value ?? 0).toLocaleString("de-DE");
}

function formatTypeLabel(value) {
    return String(value || "UNKNOWN_TYPE")
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeRows(raw) {
    const payload = raw?.data ?? raw ?? {};
    const rows = Array.isArray(payload)
        ? payload
        : payload?.data || payload?.rows || payload?.items || [];

    if (!Array.isArray(rows)) return [];

    return rows
        .map((row) => {
            const remediationType =
                row.remediation_type ||
                row.REMEDIATION_TYPE ||
                row.TYPE_NAME ||
                row.type_name ||
                row.name ||
                "UNKNOWN_TYPE";

            const open = toNumber(
                row.OPEN ??
                row.open ??
                row.open_cases ??
                row.current?.OPEN ??
                row.current?.open,
                0
            );

            const closed = toNumber(
                row.CLOSED ??
                row.closed ??
                row.closed_cases ??
                row.current?.CLOSED ??
                row.current?.closed,
                0
            );

            const total = toNumber(
                row.total ??
                row.TOTAL ??
                row.total_cases ??
                row.AMOUNT ??
                row.amount,
                open + closed
            );

            const steps = Array.isArray(row.steps)
                ? row.steps.map((step) => ({
                    step: `Step ${step.remediation_step ?? step.step ?? step.label ?? "?"}`,
                    OPEN: toNumber(step.OPEN ?? step.open, 0),
                    CLOSED: toNumber(step.CLOSED ?? step.closed, 0),
                }))
                : [
                    {
                        step: "Current",
                        OPEN: open,
                        CLOSED: closed,
                    },
                ];

            return {
                remediationType,
                label: formatTypeLabel(remediationType),
                total,
                open,
                closed,
                steps,
            };
        })
        .filter((row) => row.total > 0)
        .sort((a, b) => b.total - a.total);
}

export default function IssueBreakdownCards() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [rows, setRows] = useState([]);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                setLoading(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard?chart_key=cases_by_type`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();
                const normalized = normalizeRows(payload);

                if (!cancelled) {
                    setRows(normalized);
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load issue breakdown");
                    setRows([]);
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const topRows = useMemo(() => rows.slice(0, 4), [rows]);

    return (
        <section className="rd-issue-section">
            {loading ? (
                <div className="rd-visual-card">
                    <div className="remediation-dashboard-loading">
                        <span className="remediation-dashboard-loading-spinner" />
                        <span>Loading issue breakdown...</span>
                    </div>
                </div>
            ) : error ? (
                <div className="rd-visual-card">
                    <div className="remediation-dashboard-empty-state">{error}</div>
                </div>
            ) : topRows.length === 0 ? (
                <div className="rd-visual-card">
                    <div className="remediation-dashboard-empty-state">
                        No issue breakdown data available.
                    </div>
                </div>
            ) : (
                <div className="rd-issue-grid">
                    {topRows.map((item) => (
                        <article key={item.remediationType} className="rd-issue-card">
                            <div className="rd-issue-header">
                                <div>
                                    <h3 className="rd-issue-title">{item.remediationType}</h3>
                                    <div className="rd-issue-value-row">
                                        <span className="rd-issue-value">{formatInt(item.total)}</span>
                                        <span className="rd-issue-meta">Cases</span>
                                    </div>
                                </div>

                                <span className="rd-issue-alert-dot" />
                            </div>

                            <div className="rd-issue-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={item.steps}
                                        margin={{ top: 12, right: 10, left: 0, bottom: 8 }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="rgba(255,255,255,0.06)"
                                        />
                                        <XAxis
                                            dataKey="step"
                                            tick={TICK}
                                            axisLine={false}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={TICK}
                                            axisLine={false}
                                            tickLine={false}
                                            width={52}
                                            tickFormatter={formatInt}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: "#151024",
                                                border: "1px solid rgba(111, 58, 255, 0.55)",
                                                borderRadius: 8,
                                                color: "#EDEBFF",
                                            }}
                                            formatter={(value, name) => [formatInt(value), name]}
                                            labelStyle={{ color: "#EDEBFF", fontWeight: 700 }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                color: "#EDEBFF",
                                                fontSize: 12,
                                            }}
                                            formatter={(value) => (
                                                <span style={{ color: "#EDEBFF" }}>{value}</span>
                                            )}
                                        />
                                        <Bar dataKey="CLOSED" name="Closed" fill="#52EA91" />
                                        <Bar dataKey="OPEN" name="Open" fill="#FFB347" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
}